import { callT } from '~/utils/callI18n';
import { ICE_SERVERS, JOIN_ROOM_TIMEOUT_MS, VIDEOROOM_PLUGIN } from './constants';
import { bindPublisherConnectionMonitor } from './publisher';
import type { JanusCallRefs, JanusCallSetters } from './sessionRefs';
import type { JanusCallStatus, JoinRoomParams } from './types';
import { isMicAccessError, parseRoomId } from './utils';
import { acquireBrowserDefaultMicStream } from './webDevices';
import type { JanusMessaging } from './messaging';

export interface JoinRoomDeps {
  r: JanusCallRefs;
  set: JanusCallSetters;
  messaging: JanusMessaging;
  callStatus: JanusCallStatus;
  ensureSession: (wssUrl?: string) => Promise<void>;
  sendPublisherJoin: (transaction: string, withSdpOffer: boolean) => Promise<void>;
  cleanupCall: () => void;
  bindMicTrackLifecycle: (track: MediaStreamTrack) => void;
  isActiveCallSession: () => boolean;
}

function notifyMicUnavailable(r: JanusCallRefs, reason: 'not_found' | 'unplugged'): void {
  r.micDisconnectHandledRef.current = true;
  r.isMutedRef.current = true;
  r.onAudioHardwareLostRef.current?.({ target: 'mic', reason });
}

export async function executeJoinRoom(params: JoinRoomParams, deps: JoinRoomDeps): Promise<void> {
  const { r, set, messaging, callStatus, ensureSession, sendPublisherJoin, cleanupCall, bindMicTrackLifecycle, isActiveCallSession } =
    deps;
  const { sendJanus } = messaging;

  if (callStatus === 'oncall' || callStatus === 'joining') {
    return;
  }

  const roomId = parseRoomId(params.room);
  r.roomIdRef.current = roomId;
  r.displayNameRef.current = params.display;
  if (params.wssUrl) {
    r.signalingUrlRef.current = params.wssUrl;
  }

  if (!r.isSessionReadyRef.current || r.wsRef.current?.readyState !== WebSocket.OPEN) {
    r.isSessionReadyRef.current = false;
    await ensureSession(params.wssUrl);
  }

  if (r.handleIdRef.current && r.sessionIdRef.current) {
    sendJanus({
      janus: 'detach',
      session_id: r.sessionIdRef.current,
      handle_id: r.handleIdRef.current,
      transaction: 'detach',
    });
    r.handleIdRef.current = null;
  }

  if (!r.handleIdRef.current) {
    await new Promise<void>((resolve, reject) => {
      r.ensureSessionResolveRef.current = resolve;
      r.ensureSessionRejectRef.current = reject;

      sendJanus({
        janus: 'attach',
        session_id: r.sessionIdRef.current,
        plugin: VIDEOROOM_PLUGIN,
        transaction: 'attach',
      });
    });
  }

  set.setCallStatus('joining');
  set.setError(null);

  try {
    let stream: MediaStream | null = null;

    try {
      stream = await acquireBrowserDefaultMicStream();
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !r.isMutedRef.current;
      });
      r.micDisconnectHandledRef.current = false;
      stream.getAudioTracks().forEach((track) => {
        bindMicTrackLifecycle(track);
      });
    } catch (err: unknown) {
      if (!isMicAccessError(err)) {
        throw err;
      }
      notifyMicUnavailable(r, 'not_found');
    }

    r.localStreamRef.current = stream;
    set.setLocalStream(stream);

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    bindPublisherConnectionMonitor(r, pc, isActiveCallSession);
    r.publisherPcRef.current = pc;

    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        pc.addTrack(track, stream!);
      });
    }

    pc.onicecandidate = (e) => {
      if (r.sessionIdRef.current && r.handleIdRef.current) {
        sendJanus({
          janus: 'trickle',
          session_id: r.sessionIdRef.current,
          handle_id: r.handleIdRef.current,
          candidate: e.candidate ? e.candidate : { completed: true },
        });
      }
    };

    let joinTimeoutId: ReturnType<typeof setTimeout> | null = null;
    await new Promise<void>((resolve, reject) => {
      r.joinRoomResolveRef.current = () => {
        if (joinTimeoutId) clearTimeout(joinTimeoutId);
        resolve();
      };
      r.joinRoomRejectRef.current = (err: Error) => {
        if (joinTimeoutId) clearTimeout(joinTimeoutId);
        reject(err);
      };

      void (async () => {
        try {
          await sendPublisherJoin('join-pub', true);
        } catch (joinErr) {
          r.joinRoomRejectRef.current?.(
            joinErr instanceof Error ? joinErr : new Error(String(joinErr))
          );
        }
      })();

      joinTimeoutId = setTimeout(() => {
        if (r.joinRoomRejectRef.current) {
          r.joinRoomRejectRef.current(
            new Error(callT('errors.joinRoomTimeout', r.translateCallRef.current))
          );
          cleanupCall();
        }
      }, JOIN_ROOM_TIMEOUT_MS);
    });
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message : callT('errors.joinRoomFailed', r.translateCallRef.current);
    cleanupCall();
    throw err instanceof Error ? err : new Error(message);
  }
}
